/* DRINK MILK: product page — first photo renders in from pixels, magnifier on desktop, swipe counter on phones,
   size choice with the saved size picked, add to cart, mini size calculator, the description's highlights as chips,
   and "complete the look". Ported from the prototype (wedding-site/src/product.js). */
(function () {
  const page = document.querySelector('.dm-pdp');
  if (!page) return;
  const gsap = window.gsap;
  const ScrollTrigger = window.ScrollTrigger;
  gsap.registerPlugin(ScrollTrigger);
  const { TYPICAL_HEIGHT, recommend, savedSize, saveSize, height: fmtHeight, range: fmtRange, bindUnits } = window.DMFit;

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];
  const reducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

  const product = JSON.parse($('.pdp-data', page).textContent);
  const sizes = product.variants.map((v) => ({ id: v.id, size: String(v.option1 || v.title).toUpperCase(), available: v.available }));
  const drop = page.dataset.drop;

  /* ---------- Description: the shop writes the story as paragraphs and ends with the highlights,
     one paragraph of lines split by <br>; those lines become chips ---------- */

  const desc = $('.pdp-desc', page);
  const paras = $$('p', desc);
  const last = paras[paras.length - 1];
  if (last && /<br\s*\/?>/i.test(last.innerHTML)) {
    const lines = last.innerHTML.split(/<br\s*\/?>/i).map((l) => l.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim()).filter(Boolean);
    if (lines.length > 1) {
      $('.pdp-bullets', page).innerHTML = lines.map((l) => `<li>${l}</li>`).join('');
      last.remove();
    }
  }

  /* ---------- Gallery ---------- */

  const track = $('.pdp-gallery__track', page);
  const shots = $$('.pdp-shot', track);
  const firstShot = shots[0];
  if (reducedMotion()) firstShot?.classList.remove('is-rendering');
  // The first photo stays hidden until it renders in; never longer than 4s
  setTimeout(() => firstShot && (firstShot.querySelector('canvas') || firstShot.classList.remove('is-rendering')), 4000);

  // Phone: the counter follows the swipe
  track.addEventListener('scroll', () => {
    const i = Math.round(Math.abs(track.scrollLeft) / track.clientWidth);
    $('.pdp-gallery__now', page).textContent = Math.min(i + 1, shots.length);
  }, { passive: true });

  // First photo renders in from big pixels, like the cover on the homepage
  async function renderIn(shot) {
    const photo = $('img', shot);
    const started = performance.now();
    const skip = () => shot.classList.remove('is-rendering');
    try { await photo.decode(); } catch { return skip(); }
    // Slow connection: show the photo rather than keep it hidden
    if (performance.now() - started > 3000) return skip();
    const dpr = Math.min(devicePixelRatio || 1, 2);
    const w = shot.clientWidth;
    const h = shot.clientHeight;
    const canvas = document.createElement('canvas');
    canvas.className = 'pdp-shot__canvas';
    canvas.width = Math.round(w * dpr);
    canvas.height = Math.round(h * dpr);
    shot.append(canvas);
    const ctx = canvas.getContext('2d');
    const small = document.createElement('canvas');
    const sctx = small.getContext('2d');
    // object-fit: cover
    const s = Math.max(w / photo.naturalWidth, h / photo.naturalHeight);
    const dw = photo.naturalWidth * s;
    const dh = photo.naturalHeight * s;
    const draw = (block) => {
      const sw = Math.max(1, Math.ceil(canvas.width / block));
      const sh = Math.max(1, Math.ceil(canvas.height / block));
      small.width = sw;
      small.height = sh;
      sctx.drawImage(photo, (((w - dw) / 2) * dpr) / block, (((h - dh) / 2) * dpr) / block, (dw * dpr) / block, (dh * dpr) / block);
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(small, 0, 0, sw, sh, 0, 0, sw * block, sh * block);
    };
    shot.classList.add('is-rendering');
    const state = { p: 0 };
    draw(48);
    await gsap.to(state, {
      p: 1,
      duration: 1.3,
      ease: 'power1.inOut',
      onUpdate: () => draw(Math.max(1, Math.round((48 * (1 - state.p) ** 2) / 2) * 2)),
    });
    shot.classList.remove('is-rendering');
    gsap.to(canvas, { opacity: 0, duration: 0.3, onComplete: () => canvas.remove() });
  }

  // Desktop: a round magnifier shows the fabric up close under the pointer
  function initLens() {
    const lens = $('.pdp-lens', page);
    const ZOOM = 2.4;
    const moveLens = (e) => {
      const shot = e.target.closest('.pdp-shot');
      if (!shot || shot.classList.contains('is-rendering')) return lens.classList.remove('is-on');
      const r = shot.getBoundingClientRect();
      const g = $('.pdp-gallery', page).getBoundingClientRect();
      const x = e.clientX - r.left;
      const y = e.clientY - r.top;
      const photo = $('img', shot);
      if (lens.dataset.src !== photo.dataset.zoom) {
        lens.dataset.src = photo.dataset.zoom;
        // The sharp version loads on top of the photo already on screen, so the lens is never empty
        lens.style.backgroundImage = `url("${photo.dataset.zoom}"), url("${photo.currentSrc || photo.src}")`;
      }
      lens.style.backgroundSize = `${r.width * ZOOM}px ${r.height * ZOOM}px, ${r.width * ZOOM}px ${r.height * ZOOM}px`;
      lens.style.backgroundPosition = `${-(x * ZOOM - lens.offsetWidth / 2)}px ${-(y * ZOOM - lens.offsetHeight / 2)}px`;
      lens.style.transform = `translate(${e.clientX - g.left - lens.offsetWidth / 2}px, ${e.clientY - g.top - lens.offsetHeight / 2}px)`;
      lens.classList.add('is-on');
    };
    const hide = () => lens.classList.remove('is-on');
    track.addEventListener('pointermove', moveLens);
    track.addEventListener('pointerleave', hide);
    return () => {
      track.removeEventListener('pointermove', moveLens);
      track.removeEventListener('pointerleave', hide);
      hide();
    };
  }

  /* ---------- Size and add to cart ---------- */

  const sizesEl = $('.pdp-sizes', page);
  const addBtn = $('.pdp-add', page);
  const onlyVariant = addBtn.dataset.only ? { id: +addBtn.dataset.only, size: '', available: product.available } : null;
  const fit = savedSize();
  let picked = onlyVariant;

  if (sizesEl && fit) {
    const b = $(`.pdp-size[data-size="${fit}"]`, sizesEl);
    if (b) b.insertAdjacentHTML('beforeend', '<small class="pdp-size__fit">His size</small>');
  }

  function pick(size, { quiet } = {}) {
    picked = size;
    $$('.pdp-size', sizesEl).forEach((b) => b.setAttribute('aria-checked', String(+b.dataset.id === size?.id)));
    $('.pdp-picked', page).textContent = size ? size.size : 'Choose a size';
    $('.pdp-size-msg', page).textContent = '';
    paintAdd();
    if (size && !quiet && !reducedMotion()) {
      gsap.fromTo(`.pdp-size[data-id="${size.id}"]`, { scale: 0.88 }, { scale: 1, duration: 0.5, ease: 'back.out(3)' });
    }
  }

  function paintAdd(label) {
    if (!product.available) {
      addBtn.textContent = 'Sold out';
      addBtn.disabled = true;
      return;
    }
    addBtn.innerHTML = label || `Add to cart · <bdi dir="ltr">${addBtn.dataset.price}</bdi>`;
  }

  sizesEl?.addEventListener('click', (e) => {
    const b = e.target.closest('.pdp-size');
    if (!b) return;
    const size = sizes.find((s) => s.id === +b.dataset.id);
    if (!size.available) {
      $('.pdp-size-msg', page).textContent = `Size ${size.size} is sold out right now.`;
      return;
    }
    pick(size);
  });

  addBtn.addEventListener('click', () => {
    if (!picked) {
      $('.pdp-size-msg', page).textContent = 'Choose a size, then add to cart.';
      if (!reducedMotion()) gsap.fromTo(sizesEl, { x: -10 }, { x: 0, duration: 0.6, ease: 'elastic.out(1, 0.3)' });
      return;
    }
    window.DMCart?.add(picked.id, { from: $('.pdp-shot img', page) });
    paintAdd('Added ✓');
    setTimeout(() => paintAdd(), 1800);
  });

  // The size the calculator found is picked for the parent, if it's in stock
  const fitSize = sizes.find((s) => s.size === fit && s.available);
  if (!onlyVariant && fitSize) pick(fitSize, { quiet: true });
  else paintAdd();

  /* ---------- Mini size calculator ---------- */

  const dialog = $('.pdp-fit', page);
  const fitForm = $('.pdp-fit__form', page);
  let advised = null;
  let lenis = null;

  function updateFit() {
    $('.pdp-age-out', page).textContent = fitForm.age.value;
    $('.pdp-height-out', page).textContent = fmtHeight(+fitForm.height.value);
    const r = recommend(+fitForm.height.value);
    advised = sizes.find((s) => s.size === r.fit.size);
    const size = $('.pdp-fit__size', page);
    if (size.textContent !== r.fit.size && !reducedMotion()) gsap.fromTo(size, { y: 12, opacity: 0 }, { y: 0, opacity: 1, duration: 0.35, ease: 'power3.out' });
    size.textContent = r.fit.size;
    const btn = $('.pdp-fit-pick', page);
    if (advised?.available) {
      $('.pdp-fit__note', page).innerHTML = `Fits a height of ${fmtRange(r.lo, r.fit.max)}.`;
      btn.disabled = false;
      btn.textContent = `Choose size ${r.fit.size}`;
    } else {
      $('.pdp-fit__note', page).textContent = `Size ${r.fit.size} is sold out in this piece right now.`;
      btn.disabled = true;
      btn.textContent = 'Size sold out';
    }
  }
  fitForm.age.addEventListener('input', () => {
    fitForm.height.value = TYPICAL_HEIGHT[fitForm.age.value];
    updateFit();
  });
  fitForm.height.addEventListener('input', updateFit);
  bindUnits(dialog, updateFit);

  $('.pdp-fit-open', page)?.addEventListener('click', () => {
    updateFit();
    dialog.showModal();
    lenis?.stop();
    if (!reducedMotion()) gsap.fromTo(dialog, { y: 40, opacity: 0, scale: 0.96 }, { y: 0, opacity: 1, scale: 1, duration: 0.5, ease: 'power3.out' });
  });
  const closeFit = () => dialog.close();
  dialog.addEventListener('close', () => lenis?.start());
  $('.pdp-fit-close', page).addEventListener('click', closeFit);
  dialog.addEventListener('click', (e) => { if (e.target === dialog) closeFit(); });
  $('.pdp-fit-pick', page).addEventListener('click', () => {
    saveSize(advised.size);
    pick(advised);
    closeFit();
  });

  /* ---------- Complete the look: same drop first, four pieces ---------- */

  const lookGrid = $('.pdp-look__grid', page);
  const look = $$('.card', lookGrid).sort((a, b) => (b.dataset.drop === drop) - (a.dataset.drop === drop));
  look.slice(4).forEach((c) => c.remove());
  look.slice(0, 4).forEach((c) => lookGrid.append(c));
  $$('.size-chip', lookGrid).forEach((c) => c.classList.toggle('is-fit', c.textContent.trim() === fit));
  if (!look.length) $('.pdp-look', page).hidden = true;
  lookGrid.addEventListener('click', (e) => {
    const chip = e.target.closest('button.size-chip');
    if (!chip) return;
    chip.classList.add('is-added');
    setTimeout(() => chip.classList.remove('is-added'), 1200);
    window.DMCart?.add(+chip.dataset.id, { from: chip.closest('.card').querySelector('.card__media img') });
  });

  /* ---------- Motion ---------- */

  const shell = window.DMShell;
  const mm = gsap.matchMedia();
  mm.add({ motion: '(prefers-reduced-motion: no-preference)', fine: '(pointer: fine) and (min-width: 768px)' }, (ctx) => {
    const { motion, fine } = ctx.conditions;
    const offLens = fine ? initLens() : null;
    if (!motion) return () => offLens?.();

    lenis = new window.Lenis({ autoRaf: false });
    lenis.on('scroll', ScrollTrigger.update);
    shell?.setLenis(lenis);
    const raf = (t) => lenis.raf(t * 1000);
    gsap.ticker.add(raf);
    gsap.ticker.lagSmoothing(0);

    if (firstShot) renderIn(firstShot);
    gsap.from(['.dm-pdp .pdp-crumbs', '.dm-pdp .pdp-info__inner > *'], { y: 24, opacity: 0, duration: 0.8, stagger: 0.05, ease: 'power3.out', delay: 0.2, clearProps: 'transform,opacity' });
    gsap.from('.dm-pdp .pdp-size', { y: 14, opacity: 0, duration: 0.5, stagger: 0.05, ease: 'back.out(2)', delay: 0.6 });

    // Later photos ease up as they arrive
    shots.slice(1).forEach((shot) => {
      gsap.from($('img', shot), { scale: 1.12, duration: 1.2, ease: 'power3.out', scrollTrigger: { trigger: shot, start: 'top 85%' } });
    });
    gsap.from('.dm-pdp .pdp-look .card', { y: 60, opacity: 0, duration: 0.9, stagger: 0.08, ease: 'power3.out', scrollTrigger: { trigger: '.dm-pdp .pdp-look', start: 'top 75%' } });

    return () => {
      offLens?.();
      gsap.ticker.remove(raf);
      lenis.destroy();
      lenis = null;
      shell?.setLenis(null);
    };
  });

  addEventListener('load', () => ScrollTrigger.refresh());
})();
